/**
 * Budget Summary Component
 *
 * Displays budget information for an expense report including:
 * - Approved budget amount
 * - Total spent
 * - Remaining budget
 * - Progress bar with percentage
 * - Over-budget warnings
 */

import { formatAmount } from '../utils/formatters.js';

export interface BudgetSummaryProps {
  approvedBudget: number;
  approvedBudgetChf: number;
  totalSpent: number;
  remaining: number;
  percentUsed: number;
  isOverBudget: boolean;
  receiptCount: number;
  currency?: string;
}

/**
 * Create a budget summary component
 */
export function createBudgetSummary(props: BudgetSummaryProps): HTMLElement {
  const {
    approvedBudget: _approvedBudget,
    approvedBudgetChf,
    totalSpent,
    remaining,
    percentUsed,
    isOverBudget,
    receiptCount,
    currency: _currency = 'CHF'
  } = props;

  const container = document.createElement('div');
  container.className = 'bg-neutral-800/50 rounded-lg border border-neutral-700 backdrop-blur-sm';

  // Header
  const header = document.createElement('div');
  header.className = 'px-4 py-3 border-b border-neutral-700/50';
  header.innerHTML = `
    <h3 class="text-sm font-medium text-neutral-300">Budget Summary</h3>
  `;
  container.appendChild(header);

  // Content
  const content = document.createElement('div');
  content.className = 'p-4';

  // Stats grid
  const statsGrid = document.createElement('div');
  statsGrid.className = 'grid grid-cols-3 gap-4 mb-4';

  // Approved Budget
  const approvedStat = createStatItem(
    'Approved',
    formatAmount(approvedBudgetChf, 'CHF'),
    'text-neutral-100'
  );
  statsGrid.appendChild(approvedStat);

  // Total Spent
  const spentStat = createStatItem(
    'Spent',
    formatAmount(totalSpent, 'CHF'),
    isOverBudget ? 'text-amber-400' : 'text-neutral-100'
  );
  statsGrid.appendChild(spentStat);

  // Remaining
  const remainingStat = createStatItem(
    'Remaining',
    formatAmount(Math.abs(remaining), 'CHF'),
    isOverBudget ? 'text-red-400' : 'text-green-400'
  );
  statsGrid.appendChild(remainingStat);

  content.appendChild(statsGrid);

  // Progress bar container
  const progressContainer = document.createElement('div');
  progressContainer.className = 'space-y-2';

  // Progress bar
  const progressWrapper = document.createElement('div');
  progressWrapper.className = 'w-full bg-neutral-700 rounded-full h-2 overflow-hidden';

  const progressBar = document.createElement('div');
  progressBar.className = 'h-full transition-all duration-300 ease-out';

  // Set color based on percentage
  if (isOverBudget) {
    progressBar.className += ' bg-red-500';
  } else if (percentUsed >= 80) {
    progressBar.className += ' bg-amber-500';
  } else {
    progressBar.className += ' bg-green-500';
  }

  // Set width (cap at 100% for visual purposes even if over budget)
  progressBar.style.width = `${Math.min(percentUsed, 100)}%`;
  progressWrapper.appendChild(progressBar);

  progressContainer.appendChild(progressWrapper);

  // Percentage label
  const percentLabel = document.createElement('div');
  percentLabel.className = 'flex justify-between items-center text-xs';
  percentLabel.innerHTML = `
    <span class="text-neutral-400">${receiptCount} receipt${receiptCount !== 1 ? 's' : ''}</span>
    <span class="text-neutral-400">${percentUsed}% of budget used</span>
  `;
  progressContainer.appendChild(percentLabel);

  content.appendChild(progressContainer);

  // Over-budget warning
  if (isOverBudget) {
    const warning = createOverBudgetWarning(Math.abs(remaining), 'CHF');
    content.appendChild(warning);
  }

  container.appendChild(content);

  return container;
}

/**
 * Create a stat item for the grid
 */
function createStatItem(label: string, value: string, valueClass: string): HTMLElement {
  const item = document.createElement('div');
  item.className = 'text-center';

  const labelEl = document.createElement('div');
  labelEl.className = 'text-xs text-neutral-400 mb-1';
  labelEl.textContent = label;

  const valueEl = document.createElement('div');
  valueEl.className = `text-lg font-semibold ${valueClass}`;
  valueEl.textContent = value;

  item.appendChild(labelEl);
  item.appendChild(valueEl);

  return item;
}

/**
 * Create an over-budget warning element
 */
function createOverBudgetWarning(excessAmount: number, currency: string): HTMLElement {
  const warning = document.createElement('div');
  warning.className = 'mt-4 p-3 bg-red-900/30 border border-red-700 rounded-md flex items-start gap-2';

  // Warning icon
  const icon = document.createElement('div');
  icon.className = 'text-red-400 mt-0.5';
  icon.innerHTML = `
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
      <path d="M12 9v4"/>
      <path d="M12 17h.01"/>
    </svg>
  `;

  // Warning text
  const text = document.createElement('div');
  text.className = 'flex-1 text-sm';
  text.innerHTML = `
    <div class="font-medium text-red-300">Over budget</div>
    <div class="text-red-400/90 mt-1">
      Expenses exceed approved amount by ${formatAmount(excessAmount, currency)}.
      You may still submit, but this will be flagged for review.
    </div>
  `;

  warning.appendChild(icon);
  warning.appendChild(text);

  return warning;
}

/**
 * Update budget summary with new data
 */
export function updateBudgetSummary(
  container: HTMLElement,
  props: BudgetSummaryProps
): void {
  const newSummary = createBudgetSummary(props);
  container.replaceWith(newSummary);
}